import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBars } from 'react-icons/fa';
import { useAuthSync } from '../../hooks/useAuthSync';
import Sidebar from './Sidebar';
import Clock from '../common/Clock';
import TrialBanner from '../common/TrialBanner';
import './Layout.css';

const Layout = ({ children, signOutRedirect }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [tempExpanded, setTempExpanded] = useState(false);
  const { getUserDisplayName, getUserRole, getUsername } = useAuthSync();
  const navigate = useNavigate();

  // Detectar si es móvil y restaurar estado del sidebar
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      if (mobile) {
        // En móvil, el sidebar debe estar cerrado por defecto
        setSidebarOpen(false);
      } else {
        // En desktop, restaurar el estado guardado o usar true por defecto
        const savedState = localStorage.getItem('sidebarOpen');
        setSidebarOpen(savedState !== null ? JSON.parse(savedState) : true);
      }
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Guardar estado del sidebar en localStorage cuando cambie (solo en desktop)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
    }
  }, [sidebarOpen, isMobile]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
    setTempExpanded(false); // Reset temp expand when manually toggling
  }, []);

  const openSidebar = () => {
    setSidebarOpen(true);
    setTempExpanded(false);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setTempExpanded(false);
  };

  // Soporte para tecla de acceso rápido (Ctrl+B / Cmd+B)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        if (!isMobile) {
          toggleSidebar();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, toggleSidebar]);

  const handleTempExpand = () => {
    if (!sidebarOpen && !isMobile) {
      setTempExpanded(true);
    }
  };

  const handleTempCollapse = () => {
    setTempExpanded(false);
  };

  // Close temp expand when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tempExpanded && !e.target.closest('.sidebar')) {
        setTempExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tempExpanded]);

  return (
    <div className="layout">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar} 
        closeSidebar={closeSidebar}
        signOutRedirect={signOutRedirect}
        isMobile={isMobile}
        tempExpanded={tempExpanded}
        onTempExpand={handleTempExpand}
        onTempCollapse={handleTempCollapse}
      />
      
      {/* Main Content */}
      <div className={`main-content ${sidebarOpen || tempExpanded ? 'sidebar-open' : 'sidebar-closed'}`}>
        <div className="layout-top">
          {/* Trial Banner */}
          <TrialBanner />
          
          {/* Header */}
          <header className="layout-header">
            <div className="header-left">
              {/* Botón hamburguesa para abrir sidebar en móvil */}
              {isMobile && (
                <button className="sidebar-toggle" onClick={openSidebar}>
                  <FaBars />
                </button>
              )}
            </div>
            <div className="header-right">
              <Clock />
              <button
                type="button"
                className="header-help-btn"
                onClick={() => navigate('/ayuda')}
                aria-label="Ayuda"
              >
                <i className="fas fa-question-circle"></i>
                <span className="header-help-text">Ayuda</span>
              </button>
              <div className="user-info">
                <span className="user-name">
                  {isMobile
                    ? getUserDisplayName().split(' ').slice(0, 2).join(' ')
                    : `${getUserDisplayName()} (${getUsername()})`
                  }
                </span>
                <span className="user-role">{getUserRole()}</span>
              </div>
            </div>
          </header>
        </div>

        {/* Content Area */}
        <main className="layout-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
